/**
 * @module cercaliagl/view/VWidgetSearch
 */

import $ from 'jquery';
import { MDCSelect } from '@material/select';
import { MDCTextField } from '@material/textfield';
import { MDCTabBar } from '@material/tab-bar';
import { MDCCheckbox } from '@material/checkbox';
import BaseView, { EventType as BaseViewEventType } from './BaseView';
import { i18n } from '../dictionary';
import { createUid, isMobile } from '../util';
import {
  createCheckbox, createSelect, createTabBar, createSelectOptionItem, createFilledTextField, createTextButton,
} from './VUtils';

/**
 * @typedef {Object} AddressSearchOption
 * @property {Array.<string>} getype
 * @property {string} text
 * @property {string|undefined} ctryc
 */

/**
 * @typedef {Object} CoordinateSearchOption
 * @property {string} x
 * @property {string} y
 * @property {string} srs
 * @property {boolean} nearest
 */

/**
 * @enum {number}
 */
const Tabs = {
  ADDRESS: 0,
  COORDINATES: 1,
};

/**
 * @enum {string}
 */
const Proj = {
  EPSG4326: 'EPSG:4326',
  EPSG3857: 'EPSG:3857',
};

/**
 * @enum {string}
 */
export const EventType = {
  CLOSE_MENU: 'close_menu',
  MINIMIZE: 'minimize', // cal ?
  OPTIONS_CHANGED: 'options_changed', // country, getype
  PCODE_SUBMIT: 'pcode_submit',
  PCODE_SELECT_CANDIDATE: 'pcode_select_candidate',
  COORD_SUBMIT: 'coord_submit',
  SELECT_CAND: 'select_cand',
};

/**
 * @classdesc
 */
class VWidgetSearch extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const uid = createUid();
    const autocompleteId = `addressTabAutocomplete${uid}`;
    const jqElement = $(`
      <div class="v-widgetsearch mdc-elevation--z4">
        <div class="header">
          ${createTabBar([i18n('Address'), i18n('searchCoordinates')])}
          <div class="btn-close">
            <button type="button" class="mdc-icon-button close">
              <span class="mdc-icon-button__icon icon-close"></span>
            </button>
          </div>
        </div>
        <div class="container">
          <div class="addressTab">
            <div id="${autocompleteId}"></div>
            ${createSelect('select-country', [], null, i18n('searchSelectCountry'))}
            <div class="getype"> 
              ${createCheckbox('locality', 'ct', i18n('searchLocality'), true)}
              ${createCheckbox('address', 'st', i18n('searchAddress'), true)}
              ${createCheckbox('pois', 'p', i18n('searchPOIs'), true)}
            </div>
          </div>

          <div class="coordinatesTab">
            <div class="coords-inputs">
              ${createFilledTextField('coordx', i18n('searchXCoordinate'))}
              ${createFilledTextField('coordy', i18n('searchYCoordinate'))}
            </div>
            <div class="select-proj">
              ${createSelect('proj', [{ label: Proj.EPSG4326, value: Proj.EPSG4326 }, { label: Proj.EPSG3857, value: Proj.EPSG3857 }], Proj.EPSG4326, i18n('searchProjection'))}
            </div>
            <div class="coords-actions">
              ${createCheckbox('nearest', 'nearest', i18n('searchNearestAddress'), true)}
              ${createTextButton('searchPcodeBtn', i18n('searchSearchBtn'))}
            </div>
          </div>
        </div>
      </div>`);

    super(jqElement);

    /**
     * @private
     * @type {string}
     */
    this.autocompleteId_ = autocompleteId;

    /**
     * @private
     * @type {number}
     */
    this.currentTab_ = Tabs.ADDRESS;

    /**
     * @private
     * @type {JQuery}
     */
    this.jqAddressTab_ = this.jqElement_.find('.addressTab');

    /**
     * @private
     * @type {JQuery}
     */
    this.jqCoordinatesTab_ = this.jqElement_.find('.coordinatesTab');

    /**
     * @private
     * @type {AddressSearchOption}
     */
    this.addressSearchOption_ = {
      getype: ['st', 'ct', 'p'],
      text: null,
      ctryc: null,
    };

    /**
     * @private
     * @type {CoordinateSearchOption}
     */
    this.coordinateSearchOption_ = {
      x: null,
      y: null,
      srs: 'EPSG:4326',
      nearest: true,
    };

    /**
     * @private
     * @type {MDCSelect}
     */
    this.selectAddressCountry_ = null;

    this.initializeElements_();
  }

  /**
   * @private
   */
  initializeElements_() {
    this.on(BaseViewEventType.ATTACHED, () => this.handleAttached_());
    this.render_();
  }

  /**
   * @private
   */
  handleAttached_() {
    if (!this.jqElement_) {
      return;
    }
    this.adjustByDevice();
    // Button close
    const jqButtonClose = this.jqElement_.find('button.close');
    if (!jqButtonClose) {
      return;
    }
    jqButtonClose.click(() => this.emit(EventType.CLOSE_MENU));

    // Buttons (change tab)
    const tabBar = new MDCTabBar(this.jqElement_.find('.mdc-tab-bar').get(0));
    tabBar.listen('MDCTabBar:activated', (e) => {
      this.currentTab_ = e.detail.index;
      this.render_();
    });

    /// //////////////////////
    // Tab Address
    /// //////////////////////
    this.selectAddressCountry_ = new MDCSelect(this.jqAddressTab_.find('.mdc-select.select-country').get(0)); // Select country
    this.selectAddressCountry_.listen('MDCSelect:change', () => {
      this.addressSearchOption_.ctryc = this.selectAddressCountry_.value !== 'all' ? this.selectAddressCountry_.value : undefined;
      this.emit(EventType.OPTIONS_CHANGED, this.addressSearchOption_);
    });

    // Checkbox events
    this.jqElement_.find('.getype input[type=checkbox]').change((e) => {
      const checkbox = e.currentTarget;
      const currentValue = checkbox.value;
      if (checkbox.checked) {
        this.addressSearchOption_.getype.push(currentValue);
      } else {
        const index = this.addressSearchOption_.getype.indexOf(currentValue);
        if (index !== -1) {
          this.addressSearchOption_.getype.splice(index, 1);
        }
      }
      this.emit(EventType.OPTIONS_CHANGED, this.addressSearchOption_);
    });

    /// //////////////////////
    // Tab coordinates
    /// //////////////////////
    const inputCoordX = new MDCTextField(this.jqCoordinatesTab_.find('.mdc-text-field.coordx').get(0));
    const inputCoordY = new MDCTextField(this.jqCoordinatesTab_.find('.mdc-text-field.coordy').get(0));
    const selectCoordinateProj = new MDCSelect(this.jqCoordinatesTab_.find('.mdc-select.proj').get(0));
    const checkbox = new MDCCheckbox(this.jqCoordinatesTab_.find('.nearest').get(0));

    inputCoordX.listen('input', () => {
      this.coordinateSearchOption_.x = inputCoordX.value;
    });
    inputCoordY.listen('input', () => {
      this.coordinateSearchOption_.y = inputCoordY.value;
    });
    selectCoordinateProj.listen('MDCSelect:change', () => {
      this.coordinateSearchOption_.srs = selectCoordinateProj.value;
    });
    checkbox.listen('change', () => {
      this.coordinateSearchOption_.nearest = checkbox.checked;
    });
    this.jqCoordinatesTab_.find('button.searchPcodeBtn').click(() => {
      this.emit(EventType.COORD_SUBMIT, this.coordinateSearchOption_);
    });
  }

  /**
   * Adjust size by device
   */
  adjustByDevice() {
    this.jqElement_.css('width', !isMobile() ? 'auto' : this.getMapViewport().offsetWidth - 10);
  }

  /**
   * @param {Array.<Object>} countries Countries
   */
  setCountries(countries) {
    const selector = '.select-country ul.mdc-list';
    const jqCountriesListAddr = this.jqAddressTab_.find(selector);
    VWidgetSearch.fillCountriesByElement(jqCountriesListAddr, countries);
    this.selectAddressCountry_.layoutOptions();
  }

  /**
   * @private
   * @param {JQuery} jqElement JQuery element
   * @param {Array.<Object>} countries Countries
   */
  static fillCountriesByElement(jqElement, countries) {
    jqElement.empty();
    countries.forEach((country) => {
      jqElement.append(
        $(createSelectOptionItem({
          label: country[1],
          value: country[0],
        })),
      );
    });
  }

  /**
   * @private
   * @return {string} Autocomplete view
   */
  getAutocompleteId() {
    return this.autocompleteId_;
  }

  /**
   * Reset
   */
  reset() {
    this.currentTab_ = Tabs.ADDRESS;
    this.render_();
  }

  /**
   * @private
   */
  render_() {
    if (this.currentTab_ === Tabs.ADDRESS) {
      this.jqCoordinatesTab_.hide();
      this.jqAddressTab_.show();
    } else if (this.currentTab_ === Tabs.COORDINATES) {
      this.jqAddressTab_.hide();
      this.jqCoordinatesTab_.show();
    }
  }
}

export default VWidgetSearch;
